/**
 * `/login`. The administrator's sign-in.
 *
 * Two steps in one column: a work email, then the six-digit code the Worker
 * emails to it. Every decision (what is sent, what is shown, where the
 * reader lands) is in lib/login.ts and tested there; this file renders the
 * state and calls lib/session.ts.
 *
 * ── THE SHAPE IS THE MEMBER APP'S SIGN-IN, MEASURED ──────────────────────
 * Thomas, Sep 9 2026 night: "take some inspiration from
 * platform.pulseofp3.org just to have some harmony with the brand." So the
 * layout is the member app's own sign-in (platform.pulseofp3.org/login),
 * measured as rendered at 1431x782 and 390x844 and read against p3-web's
 * src/features/auth/login.route.tsx for what a screenshot cannot carry:
 *
 *   · a brand panel at 54%, dark maroon, the campus photograph under the
 *     member app's exact wash and crimson glow, the tagline pinned to the
 *     bottom-left with a stat row under it;
 *   · a white panel with a 392px column: a 34px "Welcome back", a 15px lede,
 *     13px sentence-case labels over 45px controls, the crimson button under
 *     its crimson shadow, the maroon promo card for the path a new visitor
 *     needs, and a 12px fine-print line;
 *   · under 1024px the panel goes, the campus photograph fills the viewport
 *     behind a frosted white card, and the title centres.
 *
 * The numbers live in gateway.css section 18 with their citations. Two
 * departures from the member app, both deliberate: the panel shows the
 * campus photograph this host already carries where the member app plays a
 * mentor video (a university is the customer here), and this host keeps its
 * nav and footer around the section, so the panel starts under the bar and
 * carries no logo of its own. The panel's headline is /overview's own
 * (`HERO` in lib/partner.ts). Its three stats are `LOGIN_STATS`, chosen by
 * Thomas apart from /overview's four (Sep 12 2026), each checkable.
 *
 * ── WHAT THE SCREEN NEVER SAYS ───────────────────────────────────────────
 * "We sent you a code." The Worker answers the same 204 for every address
 * so that an administrator list cannot be read off this form, which means
 * the screen cannot know whether anything was sent. It says a code is on
 * its way IF the address is on an institution's team, and what to do when
 * nothing arrives.
 *
 * ── ALREADY SIGNED IN ────────────────────────────────────────────────────
 * The form renders at once and the session is checked in the background;
 * a visitor who already holds a session is moved to the dashboard. The
 * other order (check first, then render) costs every signed-out visitor a
 * blank frame for nothing.
 */

import { useEffect, useReducer, useRef, useState, type FormEvent } from "react";

import { HeroAccent } from "~/app/components/GatewayHero";
import { GatewayShell, PLATFORM_URL } from "~/app/components/GatewayShell";
import { Button, Field, Input, StateCard } from "~/app/components/primitives";
import { cn } from "~/app/lib/cn";
import {
  CODE_LENGTH,
  COPY,
  INITIAL_STATE,
  describeSendFailure,
  describeVerifyFailure,
  landingFrom,
  reduce,
} from "~/app/lib/login";
import { HERO, LOGIN_STATS } from "~/app/lib/partner";
import { fetchSession, startLogin, verifyLogin } from "~/app/lib/session";
import { navigate, useHref } from "~/app/router";

/** The white P3 mark, for the promo card's tile. Self-hosted under /brand. */
const P3_MARK_WHITE = "/brand/p3-mark-white.svg";

export interface LoginProps {
  /** Off when a host renders its own chrome. */
  chrome?: boolean;
  className?: string;
}

function searchOf(href: string): string {
  const at = href.indexOf("?");
  if (at === -1) return "";
  return href.slice(at).replace(/#.*$/, "");
}

export function Login({ chrome = true, className }: LoginProps = {}) {
  const [state, dispatch] = useReducer(reduce, INITIAL_STATE);
  const [unconfigured, setUnconfigured] = useState<string | null>(null);
  const href = useHref();
  const landing = landingFrom(searchOf(href));
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchSession().then((session) => {
      if (!cancelled && session.kind === "signed-in") navigate(landing, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [landing]);

  /* Focus follows the step, so the code can be typed the moment the step
     turns, and the email field is ready again after "Use a different email". */
  useEffect(() => {
    (state.step === "code" ? codeRef : emailRef).current?.focus();
  }, [state.step]);

  useEffect(() => {
    if (state.phase === "done") navigate(landing);
  }, [state.phase, landing]);

  const busy = state.phase !== "idle";

  /* The reducer decides whether a request goes out at all (a malformed
     address never reaches the Worker), so each handler asks it first and
     only calls the Worker when the predicted state says a request is in
     flight. One source of truth for "may this be sent". */
  function requestCode(email: string) {
    void startLogin(email).then((result) => {
      if (result.ok) {
        dispatch({ type: "sent" });
        return;
      }
      const failure = describeSendFailure(result.status, result.error);
      if (failure.unconfigured) setUnconfigured(failure.message);
      dispatch({ type: "send-failed", message: failure.message });
    });
  }

  function onSend(event: FormEvent) {
    event.preventDefault();
    const next = reduce(state, { type: "send" });
    dispatch({ type: "send" });
    if (next.phase === "sending") requestCode(next.email);
  }

  function onResend() {
    const next = reduce(state, { type: "resend" });
    dispatch({ type: "resend" });
    if (next.phase === "sending") requestCode(next.email);
  }

  function onVerify(event: FormEvent) {
    event.preventDefault();
    const next = reduce(state, { type: "verify" });
    dispatch({ type: "verify" });
    if (next.phase !== "verifying") return;
    void verifyLogin(next.email, next.code).then((result) => {
      if (result.ok) {
        dispatch({ type: "verified" });
        return;
      }
      const failure = describeVerifyFailure(result.status, result.error);
      if (failure.unconfigured) setUnconfigured(failure.message);
      dispatch({ type: "verify-failed", message: failure.message, clearCode: failure.clearCode });
    });
  }

  /* When the deployment cannot sign anyone in, the card above the form says
     so once; the field does not repeat the same sentence underneath. */
  const fieldError = unconfigured !== null && state.error === unconfigured ? null : state.error;

  return (
    <GatewayShell
      current="login"
      chrome={chrome}
      mainId="login-main"
      skipLabel="Skip to sign-in"
      footer={false}
      className={cn("eg-login-page", className)}
    >
      <section className="eg-login">
        {/* The brand panel: the member app's, with the campus photograph
            where it plays a video. Hidden under 1024px. */}
        <div className="eg-login-side">
          <span className="eg-login-side-photo" aria-hidden="true" />
          <span className="eg-login-side-tint" aria-hidden="true" />
          <span className="eg-login-side-bloom" aria-hidden="true" />
          <span className="eg-login-side-shade" aria-hidden="true" />
          <div className="eg-login-side-copy">
            {/* Two lines, broken after the lead as the member app's sign-in
                breaks "The career accelerator / for the next generation"
                (Thomas, Sep 12 2026). */}
            <h2 className="eg-login-side-title">
              {HERO.lead}
              <br />
              <HeroAccent>{HERO.accent}</HeroAccent>.
            </h2>
            {/* Three stats, as on the member app's sign-in (Thomas, Sep 12
                2026). */}
            <ul className="eg-login-stats" aria-label="P3 in numbers">
              {LOGIN_STATS.map((stat) => (
                <li className="eg-login-stat" key={stat.label}>
                  <span className="eg-login-stat-v">{stat.value}</span>
                  <span className="eg-login-stat-l">{stat.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="eg-login-main">
          {/* The member app's phone ground: the campus photograph in its
              natural colours, fixed to the viewport so nothing shows under a
              browser toolbar. Off from 1024px, where the panel takes over. */}
          <span className="eg-login-mobile-bg" aria-hidden="true" />

          <div className="eg-login-card">
            {unconfigured !== null ? (
              <StateCard variant="down" title={COPY.unconfiguredTitle}>
                {unconfigured}
              </StateCard>
            ) : null}

            {state.step === "email" ? (
              <>
                <h1 className="eg-login-title">{COPY.welcome}</h1>
                <p className="eg-login-lede">{COPY.welcomeLede}</p>
                <form className="eg-login-form" onSubmit={onSend} noValidate>
                  <Field label={COPY.emailLabel} error={fieldError}>
                    <Input
                      ref={emailRef}
                      type="email"
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="none"
                      spellCheck={false}
                      placeholder={COPY.emailPlaceholder}
                      value={state.email}
                      disabled={busy}
                      onChange={(event) => dispatch({ type: "edit-email", value: event.target.value })}
                    />
                  </Field>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={busy}
                    aria-busy={state.phase === "sending" || undefined}
                  >
                    {state.phase === "sending" ? COPY.sending : COPY.send}
                  </Button>
                </form>

                <div className="eg-login-promo">
                  <div className="eg-login-promo-row">
                    <span className="eg-login-promo-ico" aria-hidden="true">
                      <img src={P3_MARK_WHITE} alt="" width={26} height={26} />
                    </span>
                    <div className="eg-login-promo-text">
                      <p className="eg-login-promo-title">{COPY.promoTitle}</p>
                      <p className="eg-login-promo-body">{COPY.promoText}</p>
                    </div>
                  </div>
                  <a className="eg-login-promo-cta" href="/checkout">
                    {COPY.promoCta} <span aria-hidden="true">{"→"}</span>
                  </a>
                </div>
              </>
            ) : (
              <>
                <h1 className="eg-login-title">{COPY.codeTitle}</h1>
                <p className="eg-login-lede">{COPY.codeLede(state.email)}</p>
                <form className="eg-login-form" onSubmit={onVerify} noValidate>
                  {state.resent && state.error === null && state.phase === "idle" ? (
                    <p className="eg-login-note" role="status">
                      {COPY.resent}
                    </p>
                  ) : null}
                  <Field label={COPY.codeLabel} hint={COPY.codeNothing} error={fieldError}>
                    <Input
                      ref={codeRef}
                      className="eg-login-code"
                      type="text"
                      name="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]*"
                      maxLength={CODE_LENGTH}
                      placeholder="000000"
                      value={state.code}
                      disabled={busy}
                      onChange={(event) => dispatch({ type: "edit-code", value: event.target.value })}
                    />
                  </Field>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={busy}
                    aria-busy={state.phase === "verifying" || undefined}
                  >
                    {state.phase === "verifying" ? COPY.verifying : COPY.verify}
                  </Button>
                  <div className="eg-login-links">
                    <button
                      type="button"
                      className="eg-login-link"
                      disabled={busy}
                      onClick={() => dispatch({ type: "change-email" })}
                    >
                      {COPY.changeEmail}
                    </button>
                    <button type="button" className="eg-login-link" disabled={busy} onClick={onResend}>
                      {state.phase === "sending" ? COPY.sending : COPY.resend}
                    </button>
                  </div>
                </form>
              </>
            )}

            <p className="eg-login-fine">
              {COPY.studentLine} <a href={PLATFORM_URL}>{COPY.studentLink}</a>.
            </p>
          </div>
        </div>
      </section>
    </GatewayShell>
  );
}

export default Login;
